import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

const CELL_CLASS = "bp2-active-cell";
const IGNORE_FOCUS_MS = 200;

type ToggleArgs = { row: number; col: number; cellEl: HTMLElement | null };

export type UseGridNavigationOptions = {
   rowsCount: number;
   colsCount: number;
   onToggleActions?: (args: ToggleArgs) => void;
   disableWhenModalShadow?: boolean;
   isEditing?: boolean;
   onEnterEditRequest?: (args: { row: number; col: number }) => void;
};

declare global {
   interface Window {
      __activeGridId?: string;
      __gridTabTransition?: boolean;
      __gridEditing?: boolean;
      __gridEditingOwnerId?: string | undefined;
   }
}

const keyFor = (row: number, col: number) => `${row}:${col}`;
const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));

export const useGridNavigation = ({
   rowsCount,
   colsCount,
   onToggleActions,
   disableWhenModalShadow = true,
   isEditing,
   onEnterEditRequest,
}: UseGridNavigationOptions) => {
   const [active, setActive] = useState<{ row: number; col: number }>({ row: 0, col: 0 });

   // Refs for current values — let stable event listeners read fresh data without re-registering
   const activeRef = useRef(active);
   activeRef.current = active;
   const isEditingRef = useRef(isEditing);
   isEditingRef.current = isEditing;

   const cellRefs = useRef<Map<string, HTMLElement>>(new Map());
   const tableRef = useRef<HTMLElement | null>(null);
   const lastCellContainerRef = useRef<HTMLElement | null>(null);

   const shiftDownRef = useRef(false);
   const shiftComboRef = useRef(false);
   const ignoreNextFocusUntilRef = useRef<number>(0);
   const focusingRef = useRef<boolean>(false);
   const suppressNextShiftSoloUntilRef = useRef<number>(0);

   const instanceIdRef = useRef<string>(
      `grid-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`,
   );
   const instanceId = instanceIdRef.current;

   const markThisTableActive = useCallback(() => {
      window.__activeGridId = instanceId;
   }, [instanceId]);

   const isActiveForThisTable = useCallback((): boolean => {
      const table = tableRef.current;
      const focused = document.activeElement as Element | null;
      if (table && focused && table.contains(focused)) return true;
      return window.__activeGridId === instanceId;
   }, [instanceId]);

   const setCellRef = useCallback(
      (row: number, col: number) => (el: HTMLElement | null) => {
         const key = keyFor(row, col);
         if (el) cellRefs.current.set(key, el);
         else cellRefs.current.delete(key);
      },
      [],
   );

   const isNavSuppressed = () =>
      disableWhenModalShadow && !!document.querySelector(".modal-shadow");

   const getCellContainer = (focusableEl: HTMLElement | null): HTMLElement | null => {
      if (!focusableEl) return null;
      if (focusableEl.tagName === "TD" || focusableEl.getAttribute("role") === "gridcell") {
         return focusableEl;
      }
      return (
         (focusableEl.closest("td, [role='gridcell']") as HTMLElement | null) ||
         focusableEl.parentElement ||
         null
      );
   };

   const setHighlight = (focusableEl: HTMLElement | null) => {
      const container = getCellContainer(focusableEl);
      if (lastCellContainerRef.current && lastCellContainerRef.current !== container) {
         lastCellContainerRef.current.classList.remove(CELL_CLASS);
      }
      if (container) {
         container.classList.add(CELL_CLASS);
         lastCellContainerRef.current = container;
      }
   };

   const clearHighlight = () => {
      if (lastCellContainerRef.current) {
         lastCellContainerRef.current.classList.remove(CELL_CLASS);
         lastCellContainerRef.current = null;
      }
   };

   const focusCell = useCallback((row: number, col: number) => {
      if (isNavSuppressed()) return;
      const el = cellRefs.current.get(keyFor(row, col)) ?? null;
      if (el) {
         focusingRef.current = true;
         el.setAttribute("tabindex", "0");
         el.focus({ preventScroll: true });
         setHighlight(el);
         el.scrollIntoView?.({ block: "nearest", inline: "nearest" });
         requestAnimationFrame(() => {
            focusingRef.current = false;
         });
      }
   }, []);

   // Reads activeRef — no dependency on active state, so this is stable
   const normalizeTabIndexes = useCallback(() => {
      const { row: activeRow, col: activeCol } = activeRef.current;
      cellRefs.current.forEach((el, key) => {
         const [rowStr, colStr] = key.split(":");
         const isActive = Number(rowStr) === activeRow && Number(colStr) === activeCol;
         el.setAttribute("tabindex", isActive ? "0" : "-1");
      });
   }, []);

   useEffect(() => {
      normalizeTabIndexes();
      focusCell(active.row, active.col);
   }, [active, normalizeTabIndexes, focusCell]);

   // Sync tabIndexes when row/col count changes — ensures the active cell gets tabIndex=0
   // after cells mount (handles deferred rendering, e.g. antd Table in ListGridNavWrapper).
   // Does NOT call focusCell to avoid stealing focus on data reload.
   useEffect(() => {
      normalizeTabIndexes();
   }, [rowsCount, colsCount, normalizeTabIndexes]);

   // Mouse click activation — stable listener (reads from refs)
   useEffect(() => {
      const tableRootEl = tableRef.current;
      if (!tableRootEl) return;

      const findWrapper = (target: Element): HTMLElement | null => {
         const byAttr = target.closest?.('[data-grid-cell="true"]') as HTMLElement | null;
         if (byAttr) return byAttr;
         const td = target.closest?.("td, [role='gridcell']") as HTMLElement | null;
         return (td?.querySelector?.('[data-grid-cell="true"]') as HTMLElement | null) ?? null;
      };

      const activateFromEvent = (e: MouseEvent) => {
         if (isNavSuppressed()) return;
         if (isEditingRef.current) return;
         markThisTableActive();

         const target = e.target as Element;
         if (target.closest("button, [role='button'], input, select, textarea, a")) return;

         const wrapper = findWrapper(target);
         const td =
            wrapper?.closest("td, [role='gridcell']") || target.closest?.("td, [role='gridcell']");
         if (!td || !tableRootEl.contains(td)) return;

         const row = Number(wrapper?.getAttribute("data-row") ?? td.getAttribute("data-row"));
         const col = Number(wrapper?.getAttribute("data-col") ?? td.getAttribute("data-col"));
         if (Number.isNaN(row) || Number.isNaN(col)) return;

         const cur = activeRef.current;
         if (cur.row !== row || cur.col !== col) setActive({ row, col });

         requestAnimationFrame(() => {
            const realCell = cellRefs.current.get(keyFor(row, col));
            const focusTarget = realCell || (td as HTMLElement);
            focusTarget.setAttribute("tabindex", "0");
            focusTarget.focus?.({ preventScroll: true });
            setHighlight(focusTarget);
         });
      };

      tableRootEl.addEventListener("mousedown", activateFromEvent, true);
      tableRootEl.addEventListener("click", activateFromEvent, true);
      return () => {
         tableRootEl.removeEventListener("mousedown", activateFromEvent, true);
         tableRootEl.removeEventListener("click", activateFromEvent, true);
      };
   }, [markThisTableActive]); // markThisTableActive is stable; isEditingRef/activeRef read from ref

   const findNextExisting = useCallback(
      (startRow: number, startCol: number, stepRow: number, stepCol: number) => {
         let row = startRow;
         let col = startCol;
         for (let i = 0; i < 500; i++) {
            row = clamp(row + stepRow, 0, Math.max(0, rowsCount - 1));
            col = clamp(col + stepCol, 0, Math.max(0, colsCount - 1));
            if (cellRefs.current.has(keyFor(row, col))) return { row, col };
            const hitRowEdge =
               stepRow !== 0 &&
               ((stepRow < 0 && row === 0) || (stepRow > 0 && row === rowsCount - 1));
            const hitColEdge =
               stepCol !== 0 &&
               ((stepCol < 0 && col === 0) || (stepCol > 0 && col === colsCount - 1));
            if (hitRowEdge || hitColEdge) break;
         }
         return null;
      },
      [rowsCount, colsCount],
   );

   // Stable move — reads active from ref instead of closing over state
   const move = useCallback(
      (stepRow: number, stepCol: number) => {
         if (!rowsCount || !colsCount) return;
         const { row, col } = activeRef.current;
         const next = findNextExisting(row, col, stepRow, stepCol);
         if (next && (next.row !== row || next.col !== col)) {
            setActive({ row: next.row, col: next.col });
         }
      },
      [rowsCount, colsCount, findNextExisting],
   );

   const focusAdjacentTable = useCallback((direction: 1 | -1) => {
      if (window.__gridEditing) return;

      const currentOuter = tableRef.current?.closest?.('[data-table-widget="true"]') ?? null;
      const tables = Array.from(
         document.querySelectorAll<HTMLElement>('[data-table-widget="true"]'),
      );
      if (!currentOuter || tables.length === 0) return;

      const idx = tables.indexOf(currentOuter as HTMLElement);
      if (idx === -1) return;

      let targetIdx = idx + direction;
      if (targetIdx < 0) targetIdx = tables.length - 1;
      if (targetIdx >= tables.length) targetIdx = 0;

      const nextTable = tables[targetIdx];
      const targetCell =
         nextTable.querySelector<HTMLElement>('[data-grid-cell="true"][tabindex="0"]') ||
         nextTable.querySelector<HTMLElement>('[data-grid-cell="true"]');

      const navEl = (
         nextTable.hasAttribute("data-bp2-grid-id")
            ? nextTable
            : nextTable.querySelector("[data-bp2-grid-id]")
      ) as HTMLElement | null;
      const nextId =
         navEl?.getAttribute("data-bp2-grid-id") ??
         ((nextTable as any).__grid_id as string | undefined);
      if (nextId) window.__activeGridId = nextId;

      (navEl ?? nextTable).focus?.({ preventScroll: false });
      requestAnimationFrame(() => {
         if (targetCell) targetCell.focus();
         else (navEl ?? nextTable).focus?.({ preventScroll: true });
      });
   }, []);

   // Stable ref for keyboard handler — avoids re-registering listeners on every navigation step
   const kbRef = useRef({
      move,
      onToggleActions,
      isActiveForThisTable,
      onEnterEditRequest,
      focusAdjacentTable,
   });
   kbRef.current = {
      move,
      onToggleActions,
      isActiveForThisTable,
      onEnterEditRequest,
      focusAdjacentTable,
   };

   // Keyboard navigation — registered once, reads from refs for fresh values
   useEffect(() => {
      const isInsideTable = () => {
         const table = tableRef.current;
         const a = document.activeElement;
         return !!table && !!a && table.contains(a as Element);
      };

      const stopAll = (event: KeyboardEvent) => {
         event.preventDefault();
         event.stopPropagation();
         (event as any).stopImmediatePropagation?.();
      };

      const onDocKeyDown = (event: KeyboardEvent) => {
         if (disableWhenModalShadow && !!document.querySelector(".modal-shadow")) return;
         if (window.__gridEditing) return;
         if (window.__gridTabTransition && !isInsideTable()) return;

         const { isActiveForThisTable, move, onEnterEditRequest, focusAdjacentTable } =
            kbRef.current;
         if (!(isInsideTable() || isActiveForThisTable())) return;
         if (isEditingRef.current) return;

         if (event.key === "Shift") {
            if (!shiftDownRef.current) {
               shiftDownRef.current = true;
               shiftComboRef.current = false;
            }
            stopAll(event);
            return;
         }

         if (shiftDownRef.current) shiftComboRef.current = true;

         switch (event.key) {
            case "Enter":
               if (typeof onEnterEditRequest === "function") {
                  stopAll(event);
                  onEnterEditRequest({ ...activeRef.current });
               }
               break;
            case "ArrowLeft":
               stopAll(event);
               move(0, -1);
               break;
            case "ArrowRight":
               stopAll(event);
               move(0, +1);
               break;
            case "ArrowUp":
               stopAll(event);
               move(-1, 0);
               break;
            case "ArrowDown":
               stopAll(event);
               move(+1, 0);
               break;
            case "Tab": {
               suppressNextShiftSoloUntilRef.current = Date.now() + 350;
               stopAll(event);
               cellRefs.current.forEach((el) => el.setAttribute("tabindex", "-1"));
               clearHighlight();
               window.__gridTabTransition = true;
               const direction = event.shiftKey ? -1 : +1;
               requestAnimationFrame(() => {
                  focusAdjacentTable(direction);
                  window.__gridTabTransition = false;
               });
               shiftDownRef.current = false;
               shiftComboRef.current = false;
               break;
            }
            default:
               break;
         }
      };

      const onDocKeyUp = (event: KeyboardEvent) => {
         if (disableWhenModalShadow && !!document.querySelector(".modal-shadow")) return;
         if (window.__gridEditing) return;
         if (window.__gridTabTransition && !isInsideTable()) return;

         const { isActiveForThisTable, onToggleActions } = kbRef.current;
         if (!(isInsideTable() || isActiveForThisTable())) return;
         if (isEditingRef.current) return;

         if (event.key === "Shift") {
            if (Date.now() < suppressNextShiftSoloUntilRef.current) {
               suppressNextShiftSoloUntilRef.current = 0;
               stopAll(event);
               shiftDownRef.current = false;
               shiftComboRef.current = false;
               return;
            }
            if (shiftDownRef.current && !shiftComboRef.current) {
               ignoreNextFocusUntilRef.current = Date.now() + IGNORE_FOCUS_MS;
               const { row, col } = activeRef.current;
               const cellEl = cellRefs.current.get(keyFor(row, col)) ?? null;
               onToggleActions?.({ row, col, cellEl });
               requestAnimationFrame(() => {
                  const el = cellRefs.current.get(keyFor(row, col));
                  el?.focus?.({ preventScroll: true });
                  if (el) setHighlight(el);
               });
            }
            stopAll(event);
            shiftDownRef.current = false;
            shiftComboRef.current = false;
         }
      };

      document.addEventListener("keydown", onDocKeyDown, true);
      document.addEventListener("keyup", onDocKeyUp, true);
      return () => {
         document.removeEventListener("keydown", onDocKeyDown, true);
         document.removeEventListener("keyup", onDocKeyUp, true);
      };
   }, []); // stable — all mutable values read from refs

   // Stamp data-row/data-col on <td> elements for click-to-cell resolution
   useEffect(() => {
      const table = tableRef.current;
      if (!table) return;
      Array.from(table.querySelectorAll("tbody tr")).forEach((tr, rowIndex) => {
         Array.from(tr.querySelectorAll("td, [role='gridcell']")).forEach((td, colIndex) => {
            td.setAttribute("data-grid-td", "true");
            td.setAttribute("data-row", String(rowIndex));
            td.setAttribute("data-col", String(colIndex));
         });
      });
   }, [rowsCount, colsCount]);

   const getTableProps = useCallback(
      () =>
         ({
            ref: (el: HTMLElement | null) => {
               tableRef.current = el;
               if (el) {
                  (el as any).__grid_id = instanceId;
                  el.setAttribute("data-bp2-grid-id", instanceId);
               }
            },
            role: "grid",
            "aria-rowcount": rowsCount,
            "aria-colcount": colsCount,
            tabIndex: -1,
            onFocusCapture: () => {
               if (isNavSuppressed()) return;
               markThisTableActive();
            },
            onMouseDownCapture: () => {
               if (isNavSuppressed()) return;
               markThisTableActive();
            },
            onBlur: (e: React.FocusEvent<HTMLDivElement>) => {
               if (!e.currentTarget.contains(e.relatedTarget)) clearHighlight();
            },
         }) as React.HTMLAttributes<HTMLDivElement>,
      [rowsCount, colsCount, instanceId, markThisTableActive],
   );

   // tabIndex is always -1 here; the active cell gets tabIndex=0 imperatively via focusCell/normalizeTabIndexes.
   // This keeps getCellProps stable (no active dependency) so decoratedColumns doesn't re-render on every keystroke.
   const getCellProps = useCallback(
      (row: number, col: number) =>
         ({
            ref: setCellRef(row, col),
            "data-grid-cell": "true",
            "data-row": row,
            "data-col": col,
            tabIndex: -1,
            onClick: (e: React.MouseEvent<HTMLSpanElement>) => {
               if (isNavSuppressed()) return;
               if (isEditingRef.current) return;
               markThisTableActive();
               if (Date.now() < ignoreNextFocusUntilRef.current) {
                  e.preventDefault();
                  e.stopPropagation();
                  (e.nativeEvent as any).stopImmediatePropagation?.();
                  return;
               }
               const cur = activeRef.current;
               if (cur.row !== row || cur.col !== col) setActive({ row, col });
               setHighlight(e.currentTarget as unknown as HTMLElement);
            },
            onFocus: (e: React.FocusEvent<HTMLSpanElement>) => {
               if (isNavSuppressed()) return;
               if (isEditingRef.current) return;
               markThisTableActive();
               if (focusingRef.current || Date.now() < ignoreNextFocusUntilRef.current) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
               }
               const cur = activeRef.current;
               if (cur.row !== row || cur.col !== col) setActive({ row, col });
               setHighlight(e.currentTarget as unknown as HTMLElement);
            },
         }) as React.HTMLAttributes<HTMLSpanElement> & { ref: (el: HTMLElement | null) => void },
      [setCellRef, markThisTableActive],
   );

   return useMemo(
      () => ({ setActive, getTableProps, getCellProps, focusCell }),
      [setActive, getTableProps, getCellProps, focusCell],
   );
};
